import { CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  FindingActionsRow,
  FindingDetails,
  SeverityBadge,
  SOLVE_TOOLTIP,
} from "@/pages/tina/site-health/components/finding-card";
import { checkLabel } from "@/pages/tina/site-health/lib/check-labels";
import type { SiteHealthGroup } from "@/pages/tina/site-health/lib/group-findings";
import type { SiteHealthFindingRow } from "@/types/tina";

interface PlatformCardProps {
  group: SiteHealthGroup;
  now: Date;
  onSnooze: (findings: SiteHealthFindingRow[]) => void;
  onSolve: (findings: SiteHealthFindingRow[]) => void;
  /** Whether a Solved click for exactly this set of findings is still in flight. */
  isSolving: (findings: SiteHealthFindingRow[]) => boolean;
  onDisable: (findings: SiteHealthFindingRow[]) => void;
  onDiscoverTest: (site: string) => void;
}

/** One card for 2+ sites of one platform with the same check — one root cause, one card. */
export function PlatformCard({
  group,
  now,
  onSnooze,
  onSolve,
  isSolving,
  onDisable,
  onDiscoverTest,
}: PlatformCardProps) {
  return (
    <Card className={cn(group.severity === "red" ? "border-red-500/40" : "border-amber-500/40")}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <SeverityBadge severity={group.severity} />
          <span>{`${group.platform} · ${group.findings.length} sites · ${checkLabel(group.checkName)}`}</span>
        </CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onSnooze(group.findings)}>
            Snooze all
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSolve(group.findings)}
            disabled={isSolving(group.findings)}
            title={SOLVE_TOOLTIP}
          >
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
            Solve all
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDisable(group.findings)}>
            Disable all
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col divide-y p-0">
        {group.findings.map((f) => (
          <div key={f.id} className="flex flex-col gap-2 px-4 py-3">
            <FindingDetails finding={f} now={now} />
            <FindingActionsRow
              onSnooze={() => onSnooze([f])}
              onSolve={() => onSolve([f])}
              solveBusy={isSolving([f])}
              onDisable={() => onDisable([f])}
              onDiscoverTest={() => onDiscoverTest(f.site)}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
